import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Info, AlertTriangle, CheckCircle2, Save, Mail, FileDown } from "lucide-react";
import { saveAuditDraft, getAuditDraft } from "@/lib/api";
import { useAwakeMember } from "@/lib/awakeContext";

const LOCAL_EMAIL_KEY = "audit_form_email";
const LOCAL_DRAFT_KEY = "audit_form_draft"; // legacy local fallback (kept for offline / unsent state)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const FREQUENCY_OPTIONS = [
  "Daily",
  "Several times a week",
  "Weekly",
  "Every 2 weeks",
  "Monthly",
  "Less than monthly",
  "I don't really post",
];

const FORMAT_OPTIONS = [
  "Long-form text",
  "Short text",
  "Native video",
  "Carousel / PDF",
  "Shared link",
  "Polls",
  "Repost with commentary",
  "Other",
];

const PROFILE_PIC_OPTIONS = [
  "Yes — recent, professional headshot",
  "Yes — casual / lifestyle photo",
  "Yes — older / outdated photo",
  "Default avatar / no photo",
];

const CONNECTION_BANDS = [
  "Under 500",
  "500 – 1,000",
  "1,000 – 5,000",
  "5,000 – 30,000",
  "30,000+ / Creator-tier",
];

export default function AuditForm() {
  const navigate = useNavigate();
  const { isMember, memberEmail, memberPlan, dashboardUrl } = useAwakeMember();
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [email, setEmail] = useState("");
  const [headline, setHeadline] = useState("");
  const [about, setAbout] = useState("");
  const [experience, setExperience] = useState("");
  const [postFrequency, setPostFrequency] = useState("");
  const [postFormats, setPostFormats] = useState([]);
  const [post1, setPost1] = useState("");
  const [post2, setPost2] = useState("");
  const [post3, setPost3] = useState("");
  // New "invisible signals" inputs — fix for evidence-free guessing
  const [profilePicture, setProfilePicture] = useState("");
  const [connectionBand, setConnectionBand] = useState("");
  const [networkAudience, setNetworkAudience] = useState("");
  const [skills, setSkills] = useState("");
  const [recommendationsReceived, setRecommendationsReceived] = useState("");
  const [recommendationsGiven, setRecommendationsGiven] = useState("");
  const [education, setEducation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Draft persistence state
  const [restorePrompt, setRestorePrompt] = useState(null); // { email, draft, updated_at } | null
  const [restoreDismissed, setRestoreDismissed] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const lastFetchedEmailRef = useRef("");

  const applyDraft = (d) => {
    setLinkedinUrl(d.linkedin_url || "");
    setHeadline(d.headline || "");
    setAbout(d.about || "");
    setExperience(d.experience || "");
    setPostFrequency(d.post_frequency || "");
    setPostFormats(Array.isArray(d.post_formats) ? d.post_formats : []);
    const samples = Array.isArray(d.post_samples) ? d.post_samples : [];
    setPost1(samples[0] || "");
    setPost2(samples[1] || "");
    setPost3(samples[2] || "");
    setProfilePicture(d.profile_picture || "");
    setConnectionBand(d.connection_band || "");
    setNetworkAudience(d.network_audience || "");
    setSkills(d.skills || "");
    setRecommendationsReceived(d.recommendations_received || "");
    setRecommendationsGiven(d.recommendations_given || "");
    setEducation(d.education || "");
  };

  // On mount: restore last-used email from localStorage + any local form snapshot.
  // Awake member email always wins — it's the verified ground truth.
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem(LOCAL_EMAIL_KEY);
      if (savedEmail) setEmail(savedEmail);
      // Prefer localStorage (survives tab close); fall back to legacy sessionStorage entry.
      const raw =
        localStorage.getItem(LOCAL_DRAFT_KEY) ||
        sessionStorage.getItem(LOCAL_DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setLinkedinUrl(d.linkedinUrl || "");
        if (!savedEmail) setEmail(d.email || "");
        setHeadline(d.headline || "");
        setAbout(d.about || "");
        setExperience(d.experience || "");
        setPostFrequency(d.postFrequency || "");
        setPostFormats(d.postFormats || []);
        setPost1(d.post1 || "");
        setPost2(d.post2 || "");
        setPost3(d.post3 || "");
      }
    } catch {}
  }, []);

  // If user arrives as an Awake member, pin the email to the verified one.
  // This overrides any stored email — members shouldn't be retyping anything.
  useEffect(() => {
    if (memberEmail) {
      setEmail(memberEmail);
      try {
        localStorage.setItem(LOCAL_EMAIL_KEY, memberEmail.toLowerCase());
      } catch {}
    }
  }, [memberEmail]);

  // When email becomes valid (and changes), try to fetch a server-side draft.
  // If the fetch fails (network blip), surface a visible error so the user can
  // re-try manually — silent failure was causing users to think their drafts
  // were gone when actually the server had them.
  const [draftFetchError, setDraftFetchError] = useState(false);
  // Token used to ignore stale fetch responses (race-condition guard) — only the
  // most-recently-fired fetch is allowed to write back to state.
  const fetchTokenRef = useRef(0);

  const fetchAndRestoreDraft = useCallback(async (force = false) => {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) return;
    if (!force && lastFetchedEmailRef.current === e) return;
    lastFetchedEmailRef.current = e;
    const token = ++fetchTokenRef.current;
    setDraftFetchError(false);
    try {
      localStorage.setItem(LOCAL_EMAIL_KEY, e);
    } catch {}
    try {
      const res = await getAuditDraft(e);
      // Stale response — a newer fetch has been issued, ignore this one entirely.
      if (token !== fetchTokenRef.current) return;
      if (res?.found && res.draft) {
        const d = res.draft;
        // Ignore empty drafts (autosaves of just-an-email pollute the server with
        // hollow records — don't surface those to the user as a "previous submission").
        const draftHasContent =
          (d.headline && d.headline.trim()) ||
          (d.about && d.about.trim()) ||
          (d.experience && d.experience.trim()) ||
          (Array.isArray(d.post_samples) && d.post_samples.some((s) => s && s.trim())) ||
          (d.post_frequency && d.post_frequency.trim()) ||
          (Array.isArray(d.post_formats) && d.post_formats.length > 0);
        if (!draftHasContent) return;
        const localHasContent =
          headline || about || experience || post1 || post2 || post3 || postFrequency || postFormats.length > 0;
        if (!localHasContent || force) {
          applyDraft(d);
          setDraftSavedAt(d.updated_at || null);
          toast.success("Welcome back — we restored your last submission.");
          setRestorePrompt(null);
        } else {
          setRestorePrompt({ draft: d, updated_at: d.updated_at });
        }
      }
    } catch {
      // Stale failure — ignore if a newer fetch is in flight (it may yet succeed).
      if (token !== fetchTokenRef.current) return;
      // Network / server issue — show the visible "couldn't load" callout so
      // the user can retry. Their server-side draft is still safe.
      setDraftFetchError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Debounce the email-based fetch so we don't fire one call per keystroke.
  // A 600ms gap after the user stops typing is enough to collapse 10+ chars of
  // typing into a single network call, eliminating the race entirely.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchAndRestoreDraft(false);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const buildDraftPayload = () => ({
    email: email.trim().toLowerCase(),
    linkedin_url: linkedinUrl.trim(),
    headline: headline.trim(),
    about: about.trim(),
    experience: experience.trim(),
    post_frequency: postFrequency,
    post_formats: postFormats,
    post_samples: [post1, post2, post3].map((p) => p.trim()).filter(Boolean),
    profile_picture: profilePicture,
    connection_band: connectionBand,
    network_audience: networkAudience.trim(),
    skills: skills.trim(),
    recommendations_received: recommendationsReceived,
    recommendations_given: recommendationsGiven,
    education: education.trim(),
  });

  // Auto-save snapshot to localStorage (survives tab close, refresh, even browser restart).
  // Saved immediately even without valid email so partially-typed work isn't lost on crash.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          LOCAL_DRAFT_KEY,
          JSON.stringify({
            linkedinUrl,
            email,
            headline,
            about,
            experience,
            postFrequency,
            postFormats,
            post1,
            post2,
            post3,
            profilePicture,
            connectionBand,
            networkAudience,
            skills,
            recommendationsReceived,
            recommendationsGiven,
            education,
          })
        );
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [linkedinUrl, email, headline, about, experience, postFrequency, postFormats, post1, post2, post3, profilePicture, connectionBand, networkAudience, skills, recommendationsReceived, recommendationsGiven, education]);

  // Debounced server-side draft save. Only fires when:
  //   1) email is valid
  //   2) restore prompt is not currently shown (don't overwrite saved draft mid-decision)
  //   3) the form has ANY meaningful content (don't pollute server with email-only stubs)
  useEffect(() => {
    if (!EMAIL_RE.test(email.trim())) return;
    if (restorePrompt) return;
    const hasContent =
      headline.trim() ||
      about.trim() ||
      experience.trim() ||
      post1.trim() ||
      post2.trim() ||
      post3.trim() ||
      postFrequency ||
      postFormats.length > 0 ||
      skills.trim() ||
      connectionBand ||
      profilePicture ||
      education.trim();
    if (!hasContent) return;
    const t = setTimeout(async () => {
      try {
        setSavingDraft(true);
        const res = await saveAuditDraft(buildDraftPayload());
        setDraftSavedAt(res?.updated_at || new Date().toISOString());
      } catch {
        // silent
      } finally {
        setSavingDraft(false);
      }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, linkedinUrl, headline, about, experience, postFrequency, postFormats, post1, post2, post3, profilePicture, connectionBand, networkAudience, skills, recommendationsReceived, recommendationsGiven, education, restorePrompt]);

  const samplePostsFilled = useMemo(
    () => [post1, post2, post3].filter((p) => p.trim().length > 8).length,
    [post1, post2, post3]
  );

  const checks = useMemo(() => {
    return {
      email: EMAIL_RE.test(email.trim()),
      headline: headline.trim().length >= 8,
      about: about.trim().length >= 120,
      experience: experience.trim().length >= 30,
      posting: !!postFrequency || postFormats.length > 0 || samplePostsFilled > 0,
    };
  }, [email, headline, about, experience, postFrequency, postFormats, samplePostsFilled]);

  const completeness = useMemo(() => {
    const filled =
      (checks.email ? 1 : 0) +
      (checks.headline ? 1 : 0) +
      (checks.about ? 1 : 0) +
      (checks.experience ? 1 : 0) +
      (checks.posting ? 1 : 0);
    return Math.round((filled / 5) * 100);
  }, [checks]);

  const togglePostFormat = (fmt) => {
    setPostFormats((prev) =>
      prev.includes(fmt) ? prev.filter((f) => f !== fmt) : [...prev, fmt]
    );
  };

  const buildPayload = () => {
    const samples = [post1, post2, post3].map((p) => p.trim()).filter(Boolean);

    const postingSection = [];
    if (postFrequency) postingSection.push(`Frequency: ${postFrequency}`);
    if (postFormats.length > 0)
      postingSection.push(`Post formats used: ${postFormats.join(", ")}`);
    if (samples.length > 0) {
      postingSection.push(
        "Sample recent posts (first line / topic):\n" +
          samples.map((s, i) => `${i + 1}. ${s}`).join("\n")
      );
    }
    const recent = postingSection.length > 0 ? postingSection.join("\n\n") : "";

    // Visible signals section — explicit user-supplied data so the model
    // doesn't have to guess for categories that aren't in the headline/about/experience text.
    const visibleSignals = [];
    if (profilePicture) visibleSignals.push(`Profile picture: ${profilePicture}`);
    if (connectionBand) visibleSignals.push(`Connections count: ${connectionBand}`);
    if (networkAudience.trim())
      visibleSignals.push(`Who's mostly in their network: ${networkAudience.trim()}`);
    if (skills.trim()) visibleSignals.push(`Top skills with endorsement counts (format: 'Skill — N'):\n${skills.trim()}`);
    const recParts = [];
    if (recommendationsReceived !== "") recParts.push(`received ${recommendationsReceived}`);
    if (recommendationsGiven !== "") recParts.push(`given ${recommendationsGiven}`);
    if (recParts.length > 0)
      visibleSignals.push(`Recommendations: ${recParts.join(", ")}`);
    if (education.trim())
      visibleSignals.push(`Education & ongoing development:\n${education.trim()}`);
    const visibleBlock = visibleSignals.join("\n");

    const profile_text = [
      headline.trim() && `HEADLINE:\n${headline.trim()}`,
      about.trim() && `ABOUT:\n${about.trim()}`,
      experience.trim() && `EXPERIENCE:\n${experience.trim()}`,
      recent && `RECENT POSTS / ACTIVITY:\n${recent}`,
      visibleBlock && `VISIBLE SIGNALS (user-reported):\n${visibleBlock}`,
    ]
      .filter(Boolean)
      .join("\n\n");

    const payload = { email: email.trim(), linkedin_url: linkedinUrl.trim(), profile_text };
    if (isMember && memberEmail) {
      payload.awake_member = {
        email: memberEmail,
        plan: memberPlan || null,
      };
    }
    return payload;
  };

  const validateAndSubmit = async (force = false) => {
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Please add a valid email so we can save and link your report.");
      return;
    }
    if (!checks.headline && !checks.about) {
      toast.error("Add at least your headline and About section.");
      return;
    }
    const { profile_text } = buildPayload();
    if (profile_text.length < 80) {
      toast.error("Profile is too short. Add your About at minimum.");
      return;
    }

    const missing = [];
    if (!checks.about) missing.push("a meaningful About section");
    if (!checks.experience) missing.push("at least one experience");
    if (!checks.posting) missing.push("posting style & frequency");

    if (missing.length >= 2 && !force) {
      setShowWarning(true);
      return;
    }

    setShowWarning(false);
    setSubmitting(true);
    sessionStorage.setItem("audit_pending", JSON.stringify(buildPayload()));
    // Persist the latest draft server-side BEFORE navigating to /run so that if the
    // LLM call crashes the user can return with the same email and recover everything.
    // We await this (best-effort, 4s timeout) — if it fails, proceed anyway so
    // the user isn't stuck.
    try {
      await Promise.race([
        saveAuditDraft(buildDraftPayload()),
        new Promise((_, rej) => setTimeout(() => rej(new Error("draft-save-timeout")), 4000)),
      ]);
    } catch {
      // localStorage snapshot is still in place — non-fatal
    }
    toast.success("Analysing your LinkedIn — this takes about 90 seconds.");
    navigate("/run");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    validateAndSubmit(false);
  };

  const handleSaveAndExit = async () => {
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Add your email first — that's how we'll remember your draft.");
      return;
    }
    try {
      setSavingDraft(true);
      const res = await saveAuditDraft(buildDraftPayload());
      setDraftSavedAt(res?.updated_at || new Date().toISOString());
      toast.success("Saved. Come back any time and re-enter your email — your inputs will be waiting.");
    } catch {
      toast.error("Couldn't save your draft. Try again in a moment.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleRestoreApply = () => {
    if (restorePrompt?.draft) applyDraft(restorePrompt.draft);
    setRestorePrompt(null);
    setRestoreDismissed(true);
    toast.success("Restored your previous submission.");
  };

  const handleRestoreDismiss = () => {
    setRestorePrompt(null);
    setRestoreDismissed(true);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="audit-form"
      id="audit-form"
      className="card-tech p-6 md:p-10 rounded-sm grid grid-cols-1 gap-6"
    >
      {/* HOW YOU'LL GET YOUR REPORT — prominent expectation-setting */}
      <div
        className="rounded-sm border-2 border-[var(--teal)] bg-[var(--paper)] p-5 md:p-6"
        data-testid="report-delivery-callout"
      >
        <div className="flex items-start gap-3">
          <FileDown size={20} className="text-[var(--teal)] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="overline" style={{ color: "var(--teal)" }}>
              How you'll get your report
            </div>
            <p className="font-display text-xl md:text-2xl tracking-tight mt-1 leading-snug">
              Your full audit appears on screen the moment it's ready — usually in around 90 seconds.
            </p>
            <ul className="text-sm text-[var(--ink-2)] mt-3 space-y-1.5 leading-relaxed">
              <li>· <span className="font-medium text-[var(--ink)]">Read it right there</span> — no waiting on inbox delivery.</li>
              <li>· <span className="font-medium text-[var(--ink)]">Save as PDF</span> with one click, or bookmark the page to return to it.</li>
              <li>· <span className="font-medium text-[var(--ink)]">We hold a copy</span> against your email — log in via Google later to access it inside The Weave.</li>
            </ul>
            <p className="text-xs text-[var(--muted)] mt-3">
              Heads up: emailed delivery isn't live yet. Bookmark or download is the way for now.
            </p>
          </div>
        </div>
      </div>

      {/* Helpful hint for copying from LinkedIn */}
      <div
        className="flex items-start gap-3 p-4 rounded-sm border border-[var(--line)] bg-[var(--bg-2)]"
        data-testid="copy-tip"
      >
        <Info size={16} className="text-[var(--teal)] mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--ink-2)] leading-relaxed">
          <span className="font-medium text-[var(--ink)]">Best done on desktop.</span>{" "}
          The LinkedIn mobile app blocks text selection. If you're on your phone, open{" "}
          <span className="font-mono-mark text-xs bg-white px-1.5 py-0.5 rounded border border-[var(--line)]">
            linkedin.com
          </span>{" "}
          in your phone's browser (not the app), tap{" "}
          <span className="font-mono-mark text-xs bg-white px-1.5 py-0.5 rounded border border-[var(--line)]">
            …see more
          </span>{" "}
          on each section, then long-press to select and copy. We can't pull this directly from your
          URL — LinkedIn blocks third-party scraping.
        </div>
      </div>

      {/* Draft-fetch failure: visible recovery affordance */}
      {draftFetchError && (
        <div
          className="rounded-sm border-2 border-[var(--red)] p-4 bg-[var(--paper)] flex items-start gap-3"
          data-testid="draft-fetch-error"
        >
          <AlertTriangle size={18} className="text-[var(--red)] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg tracking-tight">
              Couldn't load your last submission.
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              Your saved inputs are safe on our server — we just had trouble reaching them. Try again
              in a moment, or carry on and fill in the form fresh.
            </p>
            <div className="mt-3">
              <Button
                type="button"
                onClick={() => fetchAndRestoreDraft(true)}
                className="btn-primary rounded-full h-10 px-5 text-sm"
                data-testid="retry-draft-fetch-btn"
              >
                Try to restore again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Restore previous-submission prompt */}
      {restorePrompt && !restoreDismissed && (
        <div
          className="rounded-sm border-2 border-[var(--yellow-deep)] p-4 bg-[var(--bg-2)] flex items-start gap-3"
          data-testid="restore-prompt"
        >
          <Save size={18} className="text-[var(--yellow-deep)] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg tracking-tight">
              Welcome back — we kept your last submission.
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              We have your previous inputs on file for{" "}
              <span className="font-mono-mark text-xs">{email.trim().toLowerCase()}</span>. Restore them
              to amend and re-run the audit, or start fresh.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                type="button"
                onClick={handleRestoreApply}
                className="btn-primary rounded-full h-10 px-5 text-sm"
                data-testid="restore-apply-btn"
              >
                Restore my inputs
              </Button>
              <Button
                type="button"
                onClick={handleRestoreDismiss}
                variant="outline"
                className="rounded-full h-10 px-5 text-sm border-[var(--line)]"
                data-testid="restore-dismiss-btn"
              >
                Start fresh
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldLabel n="01" label="Email for the report" required ok={checks.email}>
          <Input
            data-testid="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="founder@yourcompany.com"
            readOnly={isMember}
            className={`rounded-sm h-12 border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] ${
              isMember ? "bg-[var(--bg-2)] cursor-not-allowed" : ""
            }`}
            aria-invalid={!checks.email && email.length > 0}
            required
          />
          {!checks.email && email.length > 0 && (
            <p className="text-xs text-[var(--red)] mt-1">That doesn't look like a valid email.</p>
          )}
          {isMember && (
            <p
              className="text-xs mt-1 inline-flex items-center gap-1.5"
              style={{ color: "var(--teal)" }}
              data-testid="awake-member-badge"
            >
              <CheckCircle2 size={12} /> Signed in as a Weave member · we'll attach this audit to your account
            </p>
          )}
          {!isMember && checks.email && (
            <p className="text-xs text-[var(--muted)] mt-1 inline-flex items-center gap-1">
              <Mail size={11} className="text-[var(--teal)]" />
              We use this to remember your inputs and link your audit — no email is sent.
            </p>
          )}
        </FieldLabel>
        <FieldLabel n="02" label="LinkedIn URL (optional)">
          <Input
            data-testid="linkedin-url-input"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="https://linkedin.com/in/yourname"
            className="rounded-sm h-12 border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)]"
          />
        </FieldLabel>
      </div>

      <FieldLabel
        n="03"
        label="Your headline"
        required
        ok={checks.headline}
        hint="The single line right under your name on LinkedIn."
      >
        <Input
          data-testid="headline-input"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Founder & CEO at Acme — helping B2B teams ship better outcomes"
          className="rounded-sm h-12 border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)]"
        />
      </FieldLabel>

      <FieldLabel
        n="04"
        label="Your About section"
        required
        ok={checks.about}
        recommendedChars={120}
        currentChars={about.length}
        hint={
          <>
            On LinkedIn, click <span className="font-mono-mark text-[10px]">…see more</span> under "About" to expand, then copy the lot.
          </>
        }
      >
        <Textarea
          data-testid="about-input"
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          rows={6}
          placeholder="I build companies at the intersection of..."
          className="rounded-sm border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-[14px] leading-relaxed"
        />
      </FieldLabel>

      <FieldLabel
        n="05"
        label="Top experiences"
        ok={checks.experience}
        hint="Job titles, dates, and the bullet points from your 2–3 most relevant roles."
      >
        <Textarea
          data-testid="experience-input"
          value={experience}
          onChange={(e) => setExperience(e.target.value)}
          rows={5}
          placeholder={
            "Founder & CEO at Acme (2023 — present)\n· Led 0–1 product development\n· Built first 30 customers\n\nEngineering Manager at Stripe (2017 — 2023)\n· Led the Connect platform team"
          }
          className="rounded-sm border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-[14px] leading-relaxed"
        />
      </FieldLabel>

      {/* POSTING — structured */}
      <div className="rounded-sm border border-[var(--line)] p-5 md:p-6 bg-[var(--bg-2)] space-y-6">
        <div className="flex items-center justify-between gap-2">
          <div className="overline">06 — Your posting rhythm</div>
          {checks.posting && (
            <span className="inline-flex items-center gap-1 text-[11px] font-mono-mark uppercase tracking-wider">
              <CheckCircle2 size={12} className="text-[var(--teal)]" />
              <span style={{ color: "var(--teal)" }}>ready</span>
            </span>
          )}
        </div>
        <p className="text-xs text-[var(--muted)] -mt-3">
          Tells the coach how active you are — and what formats actually fit you. The more of this
          we get, the sharper the Engagement score.
        </p>

        <div>
          <label className="text-sm font-medium block mb-2">How often do you post?</label>
          <div className="flex flex-wrap gap-2" data-testid="frequency-group">
            {FREQUENCY_OPTIONS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setPostFrequency(postFrequency === f ? "" : f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  postFrequency === f
                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                    : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--ink)]"
                }`}
                data-testid={`freq-${f.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-2">
            What formats do you use? <span className="text-xs text-[var(--muted)] font-normal">(select all that apply)</span>
          </label>
          <div className="flex flex-wrap gap-2" data-testid="format-group">
            {FORMAT_OPTIONS.map((f) => {
              const on = postFormats.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => togglePostFormat(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    on
                      ? "bg-[var(--teal)] text-white border-[var(--teal)]"
                      : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--teal)]"
                  }`}
                  data-testid={`fmt-${f.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">
            Sample posts <span className="text-xs text-[var(--muted)] font-normal">(first line or topic — text only, not links)</span>
          </label>
          <p className="text-xs text-[var(--muted)] mb-3">
            URLs alone don't help — we need to read the actual words. Paste the opening line of 2–3
            recent posts.
          </p>
          <div className="space-y-2">
            {[
              { v: post1, set: setPost1, idx: 1, ph: "We just shipped real-time tracing for LangGraph agents…" },
              { v: post2, set: setPost2, idx: 2, ph: "Why I left a big tech job to build observability for AI…" },
              { v: post3, set: setPost3, idx: 3, ph: "Hiring: senior backend engineer who loves distributed systems…" },
            ].map((row) => (
              <Textarea
                key={row.idx}
                value={row.v}
                onChange={(e) => row.set(e.target.value)}
                rows={2}
                placeholder={`Post ${row.idx} — ${row.ph}`}
                className="rounded-sm border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-[13px] leading-relaxed"
                data-testid={`post-sample-${row.idx}`}
              />
            ))}
          </div>
        </div>

        <div className="rounded-sm border-l-4 p-3 bg-[var(--paper)]" style={{ borderColor: "var(--yellow-deep)" }}>
          <div className="text-xs text-[var(--ink-2)] leading-relaxed">
            <span className="font-medium">Want to know which of your posts actually land?</span>{" "}
            That deeper engagement diagnostic (best-vs-worst post analysis, topic-by-topic performance)
            lives inside The Weave membership — it needs ongoing data, not a one-off audit.
          </div>
        </div>
      </div>

      {/* VISIBLE SIGNALS — the categories the model can't see from text alone */}
      <div className="rounded-sm border border-[var(--line)] p-5 md:p-6 bg-[var(--bg-2)] space-y-6">
        <div className="overline">07 — Visible signals we can't read from your text</div>
        <p className="text-xs text-[var(--muted)] -mt-4">
          We'd rather <span className="font-medium text-[var(--ink)]">ask you</span> than guess.
          Skip what you don't know — we'll just mark those categories "not assessed" instead of
          inventing scores.
        </p>

        <div>
          <label className="text-sm font-medium block mb-2">Your profile picture</label>
          <div className="flex flex-wrap gap-2" data-testid="profile-pic-group">
            {PROFILE_PIC_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setProfilePicture(profilePicture === opt ? "" : opt)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  profilePicture === opt
                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                    : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--ink)]"
                }`}
                data-testid={`pic-${opt.replace(/[^a-z]/gi, "-").toLowerCase().slice(0, 30)}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-2">Connection count</label>
            <div className="flex flex-wrap gap-2" data-testid="connection-band-group">
              {CONNECTION_BANDS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setConnectionBand(connectionBand === opt ? "" : opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    connectionBand === opt
                      ? "bg-[var(--teal)] text-white border-[var(--teal)]"
                      : "border-[var(--line)] bg-white text-[var(--ink-2)] hover:border-[var(--teal)]"
                  }`}
                  data-testid={`conn-${opt.replace(/[^a-z]/gi, "-").toLowerCase().slice(0, 30)}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">
              Who's mostly in your network?{" "}
              <span className="text-xs text-[var(--muted)] font-normal">(optional)</span>
            </label>
            <Input
              value={networkAudience}
              onChange={(e) => setNetworkAudience(e.target.value)}
              placeholder="e.g. UK founders, B2B operators, investors, sector peers"
              className="rounded-sm h-11 border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-sm"
              data-testid="network-audience-input"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">
            Top skills + endorsements received{" "}
            <span className="text-xs text-[var(--muted)] font-normal">(one per line: <span className="font-mono-mark">Skill — count</span>)</span>
          </label>
          <p className="text-xs text-[var(--muted)] mb-2">
            On LinkedIn, scroll to your Skills section: each skill shows the number of endorsements
            next to it. Use <span className="font-mono-mark text-[11px]">99+</span> if it says
            "99+" — it means you're highly endorsed for that one.
          </p>
          <Textarea
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
            rows={5}
            placeholder={"Founder Coaching — 99+\nCommunity Building — 47\nProduct Strategy — 23\nPublic Speaking — 8\nResilience — 3"}
            className="rounded-sm border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-[13px] leading-relaxed font-mono-mark"
            data-testid="skills-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">
            Recommendations{" "}
            <span className="text-xs text-[var(--muted)] font-normal">(written testimonials on your profile)</span>
          </label>
          <p className="text-xs text-[var(--muted)] mb-3">
            On LinkedIn → Recommendations section, you'll see "Received" and "Given" tabs with a
            count on each.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="text-xs font-medium block mb-1 text-[var(--ink-2)]">Received</label>
              <Input
                type="number"
                inputMode="numeric"
                min="0"
                value={recommendationsReceived}
                onChange={(e) => setRecommendationsReceived(e.target.value)}
                placeholder="5"
                className="rounded-sm h-10 border-[var(--line)] text-sm"
                data-testid="recommendations-received-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1 text-[var(--ink-2)]">Given</label>
              <Input
                type="number"
                inputMode="numeric"
                min="0"
                value={recommendationsGiven}
                onChange={(e) => setRecommendationsGiven(e.target.value)}
                placeholder="12"
                className="rounded-sm h-10 border-[var(--line)] text-sm"
                data-testid="recommendations-given-input"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">
            Education & ongoing development{" "}
            <span className="text-xs text-[var(--muted)] font-normal">(optional — light weight for founders, but useful when relevant)</span>
          </label>
          <p className="text-xs text-[var(--muted)] mb-2">
            Include accelerators, exec programmes, recent courses, and notable institutions —
            anything that signals credibility, sector relevance, or that you're still learning.
            Skip the 20-year-old undergrad if it doesn't add to your positioning.
          </p>
          <Textarea
            value={education}
            onChange={(e) => setEducation(e.target.value)}
            rows={4}
            placeholder={"MBA — INSEAD (2018)\nY Combinator W22\nBSc Computer Science — Imperial College London\nNewton Venture Program — current cohort"}
            className="rounded-sm border-[var(--line)] focus-visible:ring-2 focus-visible:ring-[var(--ink)] text-[13px] leading-relaxed font-mono-mark"
            data-testid="education-input"
          />
        </div>
      </div>

      {/* Completeness indicator */}
      <div className="rounded-sm border border-[var(--line)] p-4 bg-[var(--bg-2)]">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="overline">Profile completeness</div>
          <div className="font-display text-2xl">{completeness}%</div>
        </div>
        <div className="bar-track">
          <div
            className="bar-fill"
            style={{
              width: `${completeness}%`,
              background:
                completeness >= 80 ? "var(--teal)" : completeness >= 50 ? "var(--yellow-deep)" : "var(--red)",
            }}
          />
        </div>
        <p className="text-xs text-[var(--muted)] mt-3">
          Required: email, headline, About. Posting rhythm + experiences sharpen the audit.
        </p>
      </div>

      {showWarning && (
        <div
          className="rounded-sm border border-[var(--yellow-deep)] p-5 bg-[var(--bg)] flex items-start gap-3"
          data-testid="incomplete-warning"
        >
          <AlertTriangle size={18} className="text-[var(--yellow-deep)] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg tracking-tight">Heads up — your audit will be limited.</div>
            <p className="text-sm text-[var(--ink-2)] mt-2">
              You can run it now, but adding{" "}
              {[
                !checks.about && "your full About",
                !checks.experience && "top experiences",
                !checks.posting && "your posting rhythm",
              ]
                .filter(Boolean)
                .join(", ")}{" "}
              will give the coach a lot more to work with. Two more minutes of copy-paste = a much richer report.
            </p>
            <div className="flex gap-2 mt-4">
              <Button
                type="button"
                onClick={() => setShowWarning(false)}
                className="btn-primary rounded-full h-10 px-5 text-sm"
                data-testid="warning-add-more-btn"
              >
                Add more first
              </Button>
              <Button
                type="button"
                onClick={() => validateAndSubmit(true)}
                variant="outline"
                className="rounded-full h-10 px-5 text-sm border-[var(--line)]"
                data-testid="warning-continue-btn"
              >
                Continue anyway
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-[var(--muted)]">
        We never store your password. Profile text is used to generate your audit and is kept private
        to your email.
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
        <div className="flex-1 flex flex-col gap-2">
          <div className="text-sm text-[var(--ink-2)] max-w-md">
            Powered by Claude Sonnet 4.5 in The Weave's voice. Average audit takes around 90 seconds.
          </div>
          <div className="flex items-center gap-3 text-xs font-mono-mark uppercase tracking-wider">
            {savingDraft ? (
              <span className="text-[var(--muted)]">Saving draft…</span>
            ) : draftSavedAt ? (
              <span className="text-[var(--teal)] inline-flex items-center gap-1">
                <CheckCircle2 size={11} /> Draft saved · you can return any time
              </span>
            ) : (
              <span className="text-[var(--muted)]">Drafts auto-save once your email is valid</span>
            )}
            <button
              type="button"
              onClick={handleSaveAndExit}
              disabled={!checks.email || savingDraft}
              data-testid="save-and-exit-btn"
              className="inline-flex items-center gap-1 underline-offset-4 hover:underline disabled:opacity-40 disabled:no-underline text-[var(--ink-2)] normal-case tracking-normal"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              <Save size={12} /> Save & finish later
            </button>
          </div>
        </div>
        <Button
          type="submit"
          data-testid="audit-submit-btn"
          disabled={submitting}
          className="btn-red rounded-full h-12 px-8 font-medium tracking-tight"
        >
          {submitting ? "Reading…" : "Read my profile →"}
        </Button>
      </div>
    </form>
  );
}

function FieldLabel({ n, label, required, ok, hint, recommendedChars, currentChars, children }) {
  const showStatus = ok !== undefined;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="overline">
          {n} — {label}
          {required && <span className="text-[var(--red)] ml-1">*</span>}
        </label>
        {showStatus && (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono-mark uppercase tracking-wider">
            {ok ? (
              <>
                <CheckCircle2 size={12} className="text-[var(--teal)]" />
                <span style={{ color: "var(--teal)" }}>ready</span>
              </>
            ) : (
              <span style={{ color: required ? "var(--red)" : "var(--muted)" }}>
                {required ? "required" : "optional"}
              </span>
            )}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-[var(--muted)] mb-2">{hint}</p>}
      {children}
      {recommendedChars !== undefined && currentChars !== undefined && currentChars > 0 && currentChars < recommendedChars && (
        <p className="text-xs text-[var(--yellow-deep)] mt-1">
          {recommendedChars - currentChars} more characters recommended for a richer audit.
        </p>
      )}
    </div>
  );
}
